import {
  useEffect, useState, useMemo,
} from 'react';
import {
  Form, Input, Select,
} from '@skyline-platform/alchemy';
import { FormFooter } from '../FormFooter/FormFooter';
import './SourceForm.css';
import * as is from 'is';
import { SearchExistingCamera } from '../CameraForm/SearchExistingCamera';
import { SearchExistingAppliance } from '../ApplianceForm/SearchExistingAppliance';
import PropTypes from 'prop-types';
import { formatSourceData, getUpdatedSourceData } from './utils';
import { useDebouncedCallback } from 'use-debounce';
import { InfoIcon } from '@components/InfoIcon/InfoIcon';
import {
  getSources,
  saveSource,
  updateSource,
} from '@utils/graphql/creators/sources';
import { validateStringLength } from '@utils/formValidators';
import { notifications } from '@utils/notifications/index';
import { getLogger } from '@utils/general';
import { getAddrLabel } from '@pages/utils/helpers';
import { getStreamProfileLabel } from '@pages/utils/helpers';

const logger = getLogger('src.components.SourceForm');

export const SourceForm = ({
  handleClose,
  setSourceData,
  setNewCreatedSource,
  sourceData,
  lockDownFields,
  sourceCategory,
}) => {
  //Select input options
  const validProtocols = [
    'RTSP',
    'RTMP',
    'RTMPS',
    'HTTP',
    'HTTPS',
  ];
  const invalidPortCharacters = [
    'e',
    'E',
    '+',
    '-',
    '.',
  ];

  const [searchExistingField, setSearchExistingField] = useState(
    sourceData && sourceData.appliance ? 'Appliance' : 'Camera'
  );
  const [typeData, setTypeData] = useState({
    appliance: sourceData
      ? { name: getAddrLabel(sourceData?.appliance, 'management_address'), id: sourceData?.appliance?.id }
      : {},
    camera: sourceData
      ? { name: getAddrLabel(sourceData?.device, 'network_address'), id: sourceData?.device?.id }
      : lockDownFields
        ? { name: getAddrLabel(lockDownFields?.device, 'network_address'), id: lockDownFields?.device?.id }
        : {},
    isValid: sourceData ? true : false,
    newItemCreated: false,
  });
  // Inputs
  const [name, setName] = useState((sourceData?.name)?.split('|')[0].trim() || '');
  const [description, setDescription] = useState(sourceData?.description || '');
  const [inputType, setInputType] = useState(sourceData?.inputType || '');
  const [protocol, setProtocol] = useState(
    sourceData?.protocol?.toUpperCase() || ''
  );
  const [path, setPath] = useState(sourceData?.path || '');
  const [port, setPort] = useState(sourceData?.port || '');
  const [sdpInput, setSdpInput] = useState(sourceData?.sdpInput || null);
  const [sourceNameExists, setSourceNameExists] = useState(false);

  const streamProfLabel = `${sourceCategory || 'Stream'} Profile`;

  const newlyCreatedItem = useMemo(() => {
    if (typeData.newItemCreated) {
      const newItemName = typeData.camera.name || typeData.appliance.name;
      const newItemId = typeData.camera.id || typeData.appliance.id;
      if (newItemName && newItemId) {
        return { name: newItemName, id: newItemId };
      }
    }
    return {};
  }, [typeData]);

  const checkDuplicateSourceName = async (name, setDuplicateInfo) => {
    const { query, execute } = getSources(null, { name });
    query.addReturnProperties('success', 'error', 'id', 'name');
    let sourceNameDupe = false;
    const failMessage = `There was a failure retrieving data for source ${name}`;
    await execute()
      .then(({ error, data }) => {
        if (error) {
          logger.error(error, failMessage);
        }

        /**
         * When creating a new source, sourceData is undefined.
         * On edit, sourceData is defined so only show duplicate
         * error if the source name is changed to an existing one.
         */
        if (
          data?.length > 0 &&
          (is.undefined(sourceData) || sourceData.id !== data[0].id)
        ) {
          sourceNameDupe = true;
        }
      })
      .catch(() => {
        logger.error(failMessage);
      });
    setDuplicateInfo(sourceNameDupe);
  };

  const debouncedCheckDuplicateSourceName = useDebouncedCallback(
    checkDuplicateSourceName,
    300
  );

  useEffect(() => {
    debouncedCheckDuplicateSourceName(name, setSourceNameExists);
  }, [name]);

  // Logic to render appliance search or camera search
  const renderSearchExistingField = () => {
    switch (searchExistingField) {
      case 'Camera':
        return (
          <SearchExistingCamera
            modalHeader='Create New Camera'
            formData={typeData.camera}
            setFormData={(data, isValid, newItemCreated) => {
              setTypeData((prevData) => ({
                ...prevData,
                camera: data,
                isValid: isValid,
                newItemCreated: newItemCreated,
              }));
            }}
            newlyCreatedItem={newlyCreatedItem}
            lockDownFields={lockDownFields}
          />
        );
      case 'Appliance':
        return (
          <SearchExistingAppliance
            formData={typeData.appliance}
            fieldName='Appliance'
            setFormData={(data, isValid) => {
              setTypeData((prevData) => ({
                ...prevData,
                appliance: data,
                isValid: isValid,
              }));
            }}
          />
        );
    }
  };

  // Validate input fields
  const error = useMemo(() => {
    const nameValid = is.empty(name)
      ? { isValid: false, error: null }
      : sourceNameExists
        ? {
          isValid: false,
          error: `${streamProfLabel} name already exists. Please update the ${streamProfLabel.toLowerCase()} name.`,
        }
        : validateStringLength(name, 1, 32, 'Name');

    const descValid = validateStringLength(description, 0, 140, 'Description');

    const protocolValid =
      (inputType === 'SDP' && is.empty(protocol)) ||
        (inputType === 'URI' && !is.empty(protocol))
        ? { isValid: true, error: null }
        : { isValid: false, error: null }; // if inputType is not URI, don't need protocol

    const pathValid =
      inputType === 'SDP'
        ? { isValid: true, error: null } // if inputType is SDP, path should be empty
        : is.empty(path)
          ? { isValid: false, error: null }
          : validateStringLength(path, 1, 256, 'Path');

    const portValid =
      is.empty(port) ||
        (inputType === 'URI' && !isNaN(port) && port > 0 && port < 65536)
        ? { isValid: true, error: null }
        : port < 1 || port > 65535
          ? {
            isValid: false,
            error: 'Please enter a valid port number (1-65,535).',
          }
          : { isValid: false, error: 'Invalid port.' };

    const sdpInputValid =
      searchExistingField === 'Camera' && inputType === 'SDP' && sdpInput
        ? { isValid: true, error: null }
        : searchExistingField !== 'Camera' && inputType === 'SDP'
          ? {
            isValid: false,
            error: 'Type must be Camera to supply an SDP or URI.',
          }
          : inputType === 'SDP'
            ? { isValid: false, error: null }
            : { isValid: true, error: null };

    const typeDataValid =
      searchExistingField === 'Camera' &&
        typeData &&
        !is.undefined(typeData.camera.id) &&
        !is.undefined(typeData.camera.name)
        ? { isValid: true, error: null }
        : searchExistingField === 'Appliance' &&
          typeData &&
          !is.undefined(typeData.appliance.id) &&
          !is.undefined(typeData.appliance.id)
          ? { isValid: true, error: null }
          : { isValid: false, error: '' };

    return {
      name: {
        isValid: nameValid.isValid,
        error: nameValid.error,
      },
      description: {
        isValid: descValid.isValid,
        error: descValid.error,
      },
      protocol: {
        isValid: protocolValid.isValid,
        error: protocolValid.error,
      },
      path: {
        isValid: pathValid.isValid,
        error: pathValid.error,
      },
      port: {
        isValid: portValid.isValid,
        error: portValid.error,
      },
      sdpInput: {
        isValid: sdpInputValid.isValid,
        error: sdpInputValid.error,
      },
      typeData: { isValid: typeDataValid.isValid, error: typeDataValid.error },
    };
  }, [
    name,
    description,
    typeData,
    protocol,
    path,
    port,
    sdpInput,
    sourceNameExists,
    streamProfLabel,
    inputType,
    searchExistingField,
  ]);

  const allFieldsValid = useMemo(() => (
    error.name.isValid &&
    error.description.isValid &&
    error.protocol.isValid &&
    error.path.isValid &&
    error.port.isValid &&
    error.sdpInput.isValid &&
    error.typeData.isValid
  ), [error]);

  const handleSave = async (formValues) => {
    const { mutation, execute } = saveSource('create_source', formValues);
    mutation.addReturnProperties(
      'id',
      'name',
      'appliance.id',
      'appliance.name',
      'appliance.management_address',
      'device.id',
      'device.name',
      'device.network_address',
      'sdp_input',
      'scheme',
      'path',
      'port',
      'description'
    );

    const failMessage = `Failed to create ${streamProfLabel.toLowerCase()} ${formValues.name}.`;
    let source;
    await execute()
      .then(({ error, data }) => {
        if (error) {
          logger.error(error, failMessage);
        }
        if (data) {
          source = data;
        }
      })
      .catch((err) => {
        notifications.error(failMessage);
        logger.error(err, failMessage);
      });

    return source;
  };

  const handleEdit = async (sourceId, data) => {
    const { mutation, execute } = updateSource('update_source', sourceId, data);
    // These values are all needed to accurately populate the edit form after an update
    mutation.addReturnProperties(
      'id',
      'name',
      'sdp_input',
      'scheme',
      'path',
      'description',
      'port',
      'device.id',
      'device.name',
      'device.network_address',
      'appliance.id',
      'appliance.name',
      'appliance.management_address'
    );

    const failMessage = `Failed to edit ${streamProfLabel.toLowerCase()}.`;
    let source;
    await execute()
      .then(({ error, data }) => {
        if (error) {
          logger.error(error, failMessage);
        }
        if (data) {
          source = data;
        }
      })
      .catch((err) => {
        notifications.error(failMessage);
        logger.error(err, failMessage);
      });

    return formatSourceData(source);
  };

  const handleSource = async (apiChange) => {
    // Inputs are already validated via memoized error/allFieldsValid
    const source = {
      name,
      description,
      category: sourceCategory ?? 'Standard',
      ...(protocol && { scheme: protocol.toLowerCase() }), // Only include if non-empty
      ...(path && { path }),
      ...(port && { port }),
      ...(sdpInput && { sdpInput }),
      ...(typeData.camera &&
        !is.empty(typeData.camera.id) && { deviceId: typeData.camera.id }),
      ...(typeData.appliance &&
        !is.empty(typeData.appliance.id) && {
        applianceId: typeData.appliance.id,
      }),
    };

    switch (apiChange) {
      case 'newSource': {
        try {
          const savedSource = await handleSave(source);
          const formattedSource = { ...savedSource, name: getStreamProfileLabel(savedSource) };
          setSourceData(formattedSource);
          setNewCreatedSource(formattedSource);

          notifications.success(
            `Successfully created ${streamProfLabel.toLowerCase()} ${source.name}.`
          );
          handleClose();
        } catch (error) {
          const failMessage = `Failed to create ${streamProfLabel.toLowerCase()} ${source.name}.`;

          notifications.error(failMessage);
          logger.error(error, failMessage);
        }
        break;
      }
      case 'editSource': {
        const updatedData = getUpdatedSourceData(sourceData, source);
        const updatedSourceName = updatedData.name || source.name;

        try {
          const updatedSource = await handleEdit(sourceData.id, updatedData);
          // Only update the sourceData object in the Edit Appliance flow because it populates the
          // search existing offline profile input after updates are made, it is not needed in the
          // edit stream profile flow through the streams or cameras page
          if (sourceCategory === 'Offline') {
            // This will take the updated source and overwrite any previous values
            setSourceData((prevSource) => ({
              ...prevSource, ...updatedSource, name: getStreamProfileLabel(updatedSource),
            }));
          }
          notifications.success(
            `Successfully updated ${streamProfLabel.toLowerCase()} ${updatedSourceName}.`
          );
          handleClose();
        } catch (error) {
          const failMessage = `Failed to edit ${streamProfLabel.toLowerCase()}.`;
          notifications.error(failMessage);
          logger.error(error, failMessage);
        }
        break;
      }
    }
  };

  const handleCancel = () => {
    handleClose();
  };

  return (
    <gar-add-new-source>
      <Form
        footerPlacement='right'
        formTitle='New Source Form'
        footer={[
          <FormFooter
            key='footer'
            onCancel={handleCancel}
            onSubmit={() => handleSource(sourceData ? 'editSource' : 'newSource')}
            submitLabel={sourceData ? `Update ${streamProfLabel}` : `Create New ${streamProfLabel}`}
            isValid={allFieldsValid}
          />,
        ]}
      >
        <Input
          id='input-name'
          inputLabel='Name'
          helperText={`${streamProfLabel} Name`}
          value={name}
          key='input-name'
          onChange={(e) => setName(e.target.value)}
          error={error.name.isValid}
          errorMessage={error.name.isValid ? '' : error.name.error}
          required
        />
        <Input
          id='input-description'
          inputLabel='Description'
          helperText={`Description of the ${streamProfLabel}`}
          value={description}
          key='input-description'
          onChange={(e) => setDescription(e.target.value)}
          error={error.description.isValid}
          errorMessage={
            error.description.isValid ? '' : error.description.error
          }
          multiline
          rows={6}
        />
        <Select
          id='Category'
          label='Category'
          value={sourceCategory ?? 'Standard'}
          options={sourceCategory ? ['Offline'] : ['Standard']}
          disabled
        />
        <span>
          <h3>Stream Profile Origin Data</h3>
          <InfoIcon
            id='stream-profile-origin'
            label='Show description for stream profile origin data'
            desc='The Stream Profile Origin defines where the video stream comes from,
            including the network address, stream metadata, and any necessary authentication to access the video.'
          />
        </span>
        <Select
          id='sourceType'
          label='Type'
          placeholder='Camera'
          lineSeparator
          options={sourceCategory ? ['Camera'] : ['Camera', 'Appliance']}
          onSelectChange={(val) => {
            setTypeData((prevState) => ({
              ...prevState,
              appliance: {},
              camera: {},
            }));
            setSearchExistingField(val);
            if (val === 'Appliance') {
              setInputType('URI');
            } else {
              setInputType('');
            }
            setSdpInput('');
            setProtocol('');
            setPath('');
            setPort('');
          }}
          value={searchExistingField}
          disabled={lockDownFields?.device || sourceCategory === 'Offline'}
        />
        {renderSearchExistingField()}
        <span>
          <h3>Stream Profile Input Data</h3>
          <InfoIcon
            id='stream-profile-input-data'
            label='Show description for stream profile input data'
            desc='Stream Profile Input Data allows you to specify how to pull video from the Stream Profile Origin.'
          />
        </span>
        {searchExistingField === 'Camera' ? (
          <Select
            id='inputType'
            label='Input Type'
            placeholder='Select One'
            lineSeparator
            options={['SDP', 'URI']}
            onSelectChange={(val) => {
              setInputType(val);
              if (val === 'SDP') {
                setProtocol('');
                setPath('');
                setPort('');
              } else {
                setSdpInput(null);
              }
            }}
            value={inputType}
            required
          />
        ) : (
          <Select
            id='inputType'
            label='Input Type'
            value='URI'
            options={['URI']}
            disabled
          />
        )}
        {inputType === 'SDP' && (
          <Input
            id='input-sdpInput'
            inputLabel='SDP Input'
            helperText='SDP Input'
            value={sdpInput}
            key='input-sdpInput'
            onChange={(e) => setSdpInput(e.target.value)}
            error={error.sdpInput.isValid}
            errorMessage={error.sdpInput.isValid ? '' : error.sdpInput.error}
            multiline
            rows={6}
            required
          />
        )}
        {inputType === 'URI' && (
          <>
            <Select
              id='Protocol'
              label='Protocol'
              placeholder='Select One'
              lineSeparator
              options={validProtocols}
              onSelectChange={(val) => setProtocol(val)}
              value={protocol}
              required
            />
            <Input
              id='input-path'
              inputLabel='Path'
              helperText='Example: hd/test_stream'
              value={path}
              key='input-path'
              onChange={(e) => setPath(e.target.value)}
              error={error.path.isValid}
              errorMessage={error.path.isValid ? '' : error.path.error}
              required
            />
            <Input
              id='input-port'
              inputLabel='Port'
              helperText='Example: 1935'
              value={port}
              key='input-port'
              onChange={(e) => setPort(e.target.value)}
              onKeyDown={(e) => {
                if (invalidPortCharacters.includes(e.key)) {
                  e.preventDefault();
                }
              }}
              error={error.port.isValid}
              errorMessage={error.port.isValid ? '' : error.port.error}
              type='number'
            />
          </>
        )}
      </Form>
    </gar-add-new-source>
  );
};

SourceForm.propTypes = {
  editSource: PropTypes.bool,
  handleClose: PropTypes.func.isRequired,
  formData: PropTypes.object,
  setNewCreatedSource: PropTypes.func,
  setSourceData: PropTypes.func,
  sourceCategory: PropTypes.oneOf(['Standard', 'Offline']),
  sourceData: PropTypes.shape({
    id: PropTypes.string,
    name: PropTypes.string,
    description: PropTypes.string,
    category: PropTypes.string,
    device: PropTypes.object,
    appliance: PropTypes.object,
    inputType: PropTypes.string,
    protocol: PropTypes.string,
    path: PropTypes.string,
    port: PropTypes.number,
    sdpInput: PropTypes.string,
  }),
  lockDownFields: PropTypes.object,
};
